import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { inventoryApi } from '../../api';
import {
  PageHeader,
  LoadingState,
  ErrorState,
  EmptyState,
  StatusBadge,
} from '../../components/common';
import { getErrorMessage } from '../../api/client';
import { fmtCurrency, fmtMoney } from '../../utils/currency';

import { Modal } from '../../components/Modal';

/**
 * Stock Adjustments — FR-INV-02.05 / 02.06
 *
 * Write-off, damage, cycle count, found stock.
 * Pending-approval rows get inline Approve/Reject buttons.
 */
export function AdjustmentsPage() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [showCreate, setShowCreate] = useState(false);
  const [rejectingId, setRejectingId] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: ['inv', 'adjustments'],
    queryFn: () => inventoryApi.listAdjustments({ limit: 500 }),
  });

  const decideMut = useMutation({
    mutationFn: ({ id, approve, reason }: { id: string; approve: boolean; reason?: string }) =>
      inventoryApi.decideAdjustment(id, approve, reason),
    onSuccess: (data) => {
      toast.success(`Adjustment ${data.status}`);
      qc.invalidateQueries({ queryKey: ['inv', 'adjustments'] });
      setRejectingId(null);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const { kpis, filteredRows } = useMemo(() => {
    const rows = (listQuery.data ?? []) as any[];
    const pending = rows.filter((r) => r.status === 'pending_approval');
    const k = {
      total: rows.length,
      pending: pending.length,
      pendingValue: pending.reduce((a, r) => a + Math.abs(parseFloat(r.total_cost_value) || 0), 0),
      approved: rows.filter((r) => r.status === 'approved').length,
    };
    const filtered = statusFilter ? rows.filter((r) => r.status === statusFilter) : rows;
    return { kpis: k, filteredRows: filtered };
  }, [listQuery.data, statusFilter]);

  const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
    { value: '',                 label: 'All' },
    { value: 'pending_approval', label: 'Pending approval' },
    { value: 'approved',         label: 'Approved' },
    { value: 'rejected',         label: 'Rejected' },
  ];

  return (
    <div className="px-8 py-10 max-w-7xl mx-auto">
      <PageHeader
        eyebrow="Inventory"
        title="Stock adjustments"
        subtitle="Write-offs, damage, cycle-count variances, and found stock. Large values require manager approval."
        actions={
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            <PlusIcon /> New adjustment
          </button>
        }
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiTile label="Total" value={kpis.total} />
        <KpiTile label="Pending approval" value={kpis.pending}
          accent={kpis.pending > 0 ? 'amber' : 'default'} />
        <KpiTile label="Pending value" value={fmtMoney(kpis.pendingValue)}
          accent={kpis.pendingValue > 0 ? 'amber' : 'default'} />
        <KpiTile label="Approved" value={kpis.approved} accent="sage" />
      </div>

      {/* Status filter chips */}
      <div className="card p-1.5 mb-4 flex gap-1 items-center overflow-x-auto">
        {STATUS_OPTIONS.map((s) => (
          <button
            key={s.value}
            onClick={() => setStatusFilter(s.value)}
            className={clsx(
              'px-3 py-1.5 text-xs font-semibold rounded-pill transition-all whitespace-nowrap',
              statusFilter === s.value
                ? 'bg-canvas-subtle text-ink-900'
                : 'text-ink-600 hover:bg-canvas-subtle'
            )}
          >
            {s.label}
          </button>
        ))}
      </div>

      {listQuery.isLoading ? (
        <div className="card"><LoadingState rows={5} /></div>
      ) : listQuery.error ? (
        <ErrorState error={listQuery.error} />
      ) : !filteredRows.length ? (
        <div className="card">
          <EmptyState
            message={statusFilter ? `No ${statusFilter.replace('_', ' ')} adjustments` : 'No adjustments'}
            hint={statusFilter
              ? 'Try a different filter or clear it to see all.'
              : 'Record inventory changes — damage, write-offs, found stock.'}
            action={!statusFilter && (
              <button className="btn-primary" onClick={() => setShowCreate(true)}>
                <PlusIcon /> New adjustment
              </button>
            )}
          />
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Number</th>
                  <th>Date</th>
                  <th>Warehouse</th>
                  <th>Type</th>
                  <th>Reason</th>
                  <th>Created by</th>
                  <th className="text-right">Cost value</th>
                  <th>Status</th>
                  <th className="text-right"></th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((a: any) => (
                  <tr key={a.id}>
                    <td className="font-mono text-xs font-semibold text-ink-900">
                      {a.adjustment_number}
                    </td>
                    <td className="text-sm text-ink-500">
                      {new Date(a.adjustment_date).toLocaleDateString(undefined, {
                        month: 'short', day: 'numeric',
                      })}
                    </td>
                    <td className="text-sm text-ink-700 font-mono">{a.warehouse_code}</td>
                    <td>
                      <span className="badge bg-canvas-subtle text-ink-700 ring-1 ring-inset ring-ink-200 capitalize">
                        {a.type.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="text-sm text-ink-600 max-w-xs truncate">
                      {a.reason ?? <span className="text-ink-400">—</span>}
                    </td>
                    <td className="text-sm text-ink-600">{a.created_by_name}</td>
                    <td className="text-right font-mono font-semibold text-ink-900 tabular-nums">
                      ${parseFloat(a.total_cost_value).toFixed(2)}
                    </td>
                    <td><AdjStatusBadge status={a.status} /></td>
                    <td className="text-right">
                      {a.status === 'pending_approval' && (
                        <div className="flex gap-2 justify-end">
                          <button
                            className="text-xs font-medium text-ink-500 hover:text-danger transition-colors"
                            disabled={decideMut.isPending}
                            onClick={() => setRejectingId(a.id)}
                          >
                            Reject
                          </button>
                          <button
                            className="text-xs font-semibold text-sage-700 hover:text-sage-800 transition-colors"
                            disabled={decideMut.isPending}
                            onClick={() => decideMut.mutate({ id: a.id, approve: true })}
                          >
                            Approve
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showCreate && (
        <CreateAdjustmentModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            qc.invalidateQueries({ queryKey: ['inv', 'adjustments'] });
            setShowCreate(false);
          }}
        />
      )}

      {rejectingId && (
        <RejectAdjustmentDialog
          loading={decideMut.isPending}
          onClose={() => setRejectingId(null)}
          onConfirm={(reason) => decideMut.mutate({ id: rejectingId, approve: false, reason })}
        />
      )}
    </div>
  );
}

// ============================================================
// RejectAdjustmentDialog — replaces prompt()
// ============================================================
function RejectAdjustmentDialog({
  loading, onClose, onConfirm,
}: { loading: boolean; onClose: () => void; onConfirm: (reason: string | undefined) => void }) {
  const [reason, setReason] = useState('');
  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow text-danger mb-1">Inventory · Reject</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Reject adjustment?</h2>
        <p className="text-sm text-ink-500 mt-2 leading-relaxed">
          Rejected adjustments stay on record for audit trail but do not post stock movements
          or journal entries. The reason is visible to the submitter.
        </p>
      </div>
      <div className="px-6 py-5">
        <label className="label">Reason <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
        <textarea
          className="input"
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Variance too large — please recount and resubmit"
        />
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button
          className="btn-primary !bg-danger hover:!bg-red-700"
          disabled={loading}
          onClick={() => onConfirm(reason.trim() || undefined)}
        >
          {loading ? 'Rejecting…' : 'Reject adjustment'}
        </button>
      </div>
    </Modal>
  );
}

// Explicit tone mapping — 'pending_approval' is warning, 'rejected' is danger.
function AdjStatusBadge({ status }: { status: string }) {
  const tone =
    status === 'approved'          ? 'sage' :
    status === 'pending_approval'  ? 'warning' :
    status === 'rejected'          ? 'danger' :
                                     'neutral';
  return <StatusBadge status={status.replace('_', ' ')} tone={tone as any} />;
}

// ============================================================
// CreateAdjustmentModal
// ============================================================
function CreateAdjustmentModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [warehouseId, setWarehouseId] = useState('');
  const [type, setType] = useState<'decrease' | 'increase' | 'write_off' | 'damage' | 'cycle_count'>('damage');
  const [reason, setReason] = useState('');
  const [lines, setLines] = useState<Array<{ productId: string; productName?: string; sku?: string; quantity: number; unitCost?: number; notes?: string }>>([]);
  const [productSearch, setProductSearch] = useState('');

  const whQuery = useQuery({ queryKey: ['warehouses'], queryFn: inventoryApi.listWarehouses });
  const prodQuery = useQuery({
    queryKey: ['products-search', productSearch],
    queryFn: () => inventoryApi.listProducts({ q: productSearch, pageSize: 10 }),
    enabled: productSearch.length >= 2,
  });

  const createMut = useMutation({
    mutationFn: () => inventoryApi.createAdjustment({
      warehouseId,
      type,
      reason: reason || undefined,
      lines: lines.map((l) => ({
        productId: l.productId,
        quantity: l.quantity,
        unitCost: l.unitCost,
        notes: l.notes,
      })),
    }),
    onSuccess: (data) => {
      if (data.needsApproval) {
        toast.success(`Adjustment ${data.adjustmentNumber} submitted for approval (${fmtCurrency(data.totalCostValue)} > $${data.threshold} threshold)`, { duration: 6000 });
      } else {
        toast.success(`Adjustment ${data.adjustmentNumber} posted. JE: ${data.journalEntryNumber ?? 'n/a'}`);
      }
      onCreated();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const canSubmit = warehouseId && lines.length > 0 && lines.every((l) => l.quantity !== 0);

  // Rough preview (uses user-entered unit cost only; real value settled server-side)
  const previewValue = lines.reduce((a, l) => a + Math.abs(l.quantity) * (l.unitCost ?? 0), 0);

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col overflow-hidden">
      <div className="px-6 pt-6 pb-4 flex-shrink-0">
        <div className="eyebrow mb-1">Inventory</div>
        <h2 className="text-xl font-bold font-display text-ink-900">New stock adjustment</h2>
        <p className="text-sm text-ink-500 mt-1">
          Adjustments over the approval threshold will queue for manager review before posting.
        </p>
      </div>

      <div className="px-6 pb-4 grid grid-cols-2 gap-3 flex-shrink-0">
        <div>
          <label className="label">Warehouse</label>
          <select className="input" value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
            <option value="">Select warehouse…</option>
            {whQuery.data?.map((w: any) => (
              <option key={w.id} value={w.id}>{w.code} — {w.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label">Type</label>
          <select className="input" value={type} onChange={(e) => setType(e.target.value as any)}>
            <option value="damage">Damage</option>
            <option value="write_off">Write-off</option>
            <option value="cycle_count">Cycle count variance</option>
            <option value="decrease">Other decrease</option>
            <option value="increase">Found stock / increase</option>
          </select>
        </div>
      </div>

      <div className="px-6 pb-3">
        <label className="label">Reason <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
        <input className="input" value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. water damage from leak, broken glass, month-end cycle count…" />
      </div>

      <div className="px-6 flex-1 overflow-y-auto">
        <div className="eyebrow text-ink-500 mb-2">Lines ({lines.length})</div>

        <div className="border border-ink-100 rounded-xl overflow-hidden">
          <div className="p-3 border-b border-ink-100 relative">
            <div className="relative">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400 pointer-events-none">
                <circle cx="11" cy="11" r="8"/>
                <line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                className="input pl-9 text-sm"
                placeholder="Search product to add…"
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
              />
            </div>
            {prodQuery.data?.data?.length > 0 && productSearch.length >= 2 && (
              <div className="absolute left-3 right-3 top-full mt-1 bg-canvas-elevated border border-ink-200 rounded-xl shadow-lg max-h-60 overflow-y-auto z-20">
                {(prodQuery.data?.data ?? []).map((p: any) => (
                  <button
                    key={p.id}
                    className="w-full text-left px-3 py-2 hover:bg-canvas-subtle border-b border-ink-100 last:border-0 transition-colors"
                    onClick={() => {
                      setLines((prev) => [...prev, {
                        productId: p.id,
                        productName: p.name,
                        sku: p.sku,
                        quantity: type === 'increase' ? 1 : -1,
                      }]);
                      setProductSearch('');
                    }}
                  >
                    <div className="font-semibold text-ink-900 text-sm">{p.name}</div>
                    <div className="font-mono text-xs text-ink-500">{p.sku}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
          {lines.length === 0 ? (
            <div className="p-8 text-center text-sm text-ink-500">
              Search above to add products.
            </div>
          ) : (
            <table className="table text-xs">
              <thead>
                <tr>
                  <th>Product</th>
                  <th className="text-right w-24">Qty (signed)</th>
                  {type === 'increase' && <th className="text-right w-24">Unit cost</th>}
                  <th>Notes</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i}>
                    <td>
                      <div className="font-medium text-ink-900">{l.productName}</div>
                      {l.sku && <div className="font-mono text-2xs text-ink-500">{l.sku}</div>}
                    </td>
                    <td className="text-right">
                      <input
                        type="number" step="1"
                        className={clsx(
                          'input w-20 text-right tabular-nums',
                          l.quantity < 0 && 'text-danger',
                          l.quantity > 0 && 'text-sage-700',
                        )}
                        value={l.quantity}
                        onChange={(e) => {
                          const v = parseFloat(e.target.value) || 0;
                          setLines((prev) => prev.map((x, j) => j === i ? { ...x, quantity: v } : x));
                        }}
                      />
                    </td>
                    {type === 'increase' && (
                      <td className="text-right">
                        <div className="relative inline-block">
                          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-500 text-xs">$</span>
                          <input
                            type="number" step="0.01" min="0"
                            className="input w-24 pl-6 text-right tabular-nums"
                            value={l.unitCost ?? ''}
                            onChange={(e) => {
                              const v = parseFloat(e.target.value);
                              setLines((prev) => prev.map((x, j) => j === i ? { ...x, unitCost: isNaN(v) ? undefined : v } : x));
                            }}
                            placeholder="0.00"
                          />
                        </div>
                      </td>
                    )}
                    <td>
                      <input
                        className="input text-xs"
                        value={l.notes ?? ''}
                        onChange={(e) => setLines((prev) => prev.map((x, j) => j === i ? { ...x, notes: e.target.value } : x))}
                      />
                    </td>
                    <td>
                      <button
                        className="w-7 h-7 flex items-center justify-center text-ink-400 hover:text-danger hover:bg-red-50 rounded-md transition-colors"
                        onClick={() => setLines((prev) => prev.filter((_, j) => j !== i))}
                        aria-label="Remove line"
                      >✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex-shrink-0 flex items-center justify-between gap-3">
        <div>
          <div className="eyebrow text-ink-500">
            {type === 'increase' ? 'Estimated value' : 'Write-off value'}
          </div>
          <div className="font-display font-bold text-xl text-ink-900 tabular-nums tracking-tight">
            {previewValue > 0 ? `${fmtCurrency(previewValue)}` : '—'}
          </div>
        </div>
        <div className="flex gap-2">
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn-primary"
            disabled={!canSubmit || createMut.isPending}
            onClick={() => createMut.mutate()}
          >
            {createMut.isPending ? 'Posting…' : 'Post adjustment'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ============================================================
// Helpers
// ============================================================

function KpiTile({
  label, value, accent = 'default',
}: {
  label: string;
  value: number | string;
  accent?: 'default' | 'sage' | 'amber' | 'danger';
}) {
  const bar = {
    default: 'bg-ink-200',
    sage: 'bg-sage-500',
    amber: 'bg-amber-500',
    danger: 'bg-danger',
  }[accent];
  return (
    <div className="card p-5 relative overflow-hidden">
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
      <div className="eyebrow text-ink-500">{label}</div>
      <div className="mt-3 text-3xl font-bold font-display text-ink-900 tabular-nums">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </div>
  );
}

const iconProps = {
  width: 16, height: 16, viewBox: '0 0 24 24',
  fill: 'none', stroke: 'currentColor', strokeWidth: 2,
  strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
};
function PlusIcon() { return <svg {...iconProps}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>; }
